import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetSaleQuery, useRemoveCardFromSaleMutation, useUpdateSaleMutation, useAddSupplyToSaleMutation, useDeleteSaleMutation, useMarkSaleAsSoldMutation, useRevertSaleToListedMutation } from '../../../../../../entities/sales/sales.api';
import { useSelector, useDispatch } from 'react-redux';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from '../../../../../../utils/firebase/firebase.utils.js';
import { selectUser } from '../../../../../../store/user/user.selector';
import { setSystemNotification } from '../../../../../../store/system/system.action';
import { invalidateCollection } from '../../../../../../store/card/card.action';
import { selectChecklistYearDefault, selectChecklistManufacturerDefault, selectChecklistSportDefault } from '../../../../../../store/system/system.selector';
import { calculateNetProfit } from '../../../../../../utils/sales/salesMath';
import { SALE_STATUSES, normalizeSaleStatus, isSold } from '../../../../../../utils/sales/sales.constants';
import { toDateInputValue, fromDateInputValue } from '../../../../../../utils/sales/saleDates';
import { PLATFORM_OPTIONS } from '../../../../../../data/platform-data/platform-data';
import { syncGroupCodePreviewForSale } from '../../../../../../utils/firebase/groupCodes';

// components
import PrimaryBtn from '../../../../../../components/btns/primary-btn/PrimaryBtn.js';
import SecondaryBtn from '../../../../../../components/btns/secondary-btn/SecondaryBtn.js';
import CardCountPill from '../../../../../../components/card-count-pill/CardCountPill.js';
import CollapsibleChecklist from '../../../../../../components/collapsible-checklist/CollapsibleChecklist.js';
import InputBar from '../../../../../../components/input-bar/InputBar.js';
import InputTextfield from '../../../../../../components/input-textfield/InputTextfield.js';
import InputDropdown from '../../../../../../components/input-dropdown/InputDropdown.js';
import addIcon from '../../../../../../assets/system/icons/add-icon.png';
import minusIcon from '../../../../../../assets/system/icons/minus-icon.png';

import {
  ListingDetailContainer,
  Header,
  HeaderLeftContent,
  ListingTitle,
  HeaderRightContent,
  BodyLeftContent,
  OverviewDetailContent,
  LoadingSpinner,
  ErrorMessage,
  EmptyState,
  InfoRow,
  ListingStats,
  StatusControl,
  ConfirmMessage,
  MetaStrip,
  MetaItem,
  MetaLabel,
  MetaValue,
  CopyableMetaValue,
  BodyContent,
  InventoryDetailContent,
  InventorySelectionDetails,
  QtyTicker,
  QtyButton,
  QtyValue,
  CostRow,
  CostLabel,
  CostValue,
  OverviewTitle
} from './ListingDetailPage.styles';

const UNGROUPED_LABEL = 'Other Cards';

/**
 * Locates a card within the set / card type / category folders.
 * Prefers the structured cardPath; older snapshots only carry checklistPath.
 * Returns null for manually added cards, which have no checklist context.
 */
const resolveCardGrouping = (card) => {
  const { series, cardType, cardTypeCategory } = card.cardPath ?? {};
  if (series && cardType && cardTypeCategory) return [series, cardType, cardTypeCategory];

  if (card.checklistPath) {
    // /checklists/sport/year/manufacturer/series/cardType/cardTypeCategory
    const segments = card.checklistPath.replace('/checklists/', '').split('/').filter(Boolean);
    if (segments.length >= 6) return segments.slice(3, 6);
  }

  return null;
};

// Once sold, a card that never left the collection is worth surfacing —
// the release outcome is otherwise only visible in a passing notification.
const RELEASE_BADGES = {
  not_in_collection: 'NOT IN COLLECTION',
  no_card_path: 'NOT LINKED'
};

// Transforms a sale's cards into the folder structure CollapsibleChecklist renders
const transformListingCardsToHierarchy = (listingCards, showReleaseBadges = false) => {
  const hierarchy = {};

  listingCards.forEach((card, index) => {
    // Cards are stored with a singular `variation`; accept both shapes
    const variations = card.variations || (card.variation ? [card.variation] : []);

    const entry = {
      id: card.id,
      cardNumber: card.cardNumber || 'N/A',
      name: card.name || ['Unknown Player'],
      team: card.team || ['Unknown Team'],
      quantity: card.quantity || 1,
      cardAttributes: card.cardAttributes || [],
      variations,
      checklistPath: card.checklistPath,
      isCollected: card.isCollected !== undefined ? card.isCollected : variations.length > 0,
      badge: showReleaseBadges ? RELEASE_BADGES[card.releaseResult] : undefined
    };

    const cardId = card.id || `${card.cardNumber || 'unknown'}_${index}`;
    const grouping = resolveCardGrouping(card);

    // Cards with no checklist context still belong to the sale, so they get a
    // folder of their own rather than being dropped from the list.
    if (!grouping) {
      const label = card.series || UNGROUPED_LABEL;
      if (!hierarchy[label]) hierarchy[label] = {};
      hierarchy[label][cardId] = entry;
      return;
    }

    const [series, cardType, cardTypeCategory] = grouping;
    if (!hierarchy[series]) hierarchy[series] = {};
    if (!hierarchy[series][cardType]) hierarchy[series][cardType] = {};
    if (!hierarchy[series][cardType][cardTypeCategory]) {
      hierarchy[series][cardType][cardTypeCategory] = {};
    }

    hierarchy[series][cardType][cardTypeCategory][cardId] = entry;
  });

  return hierarchy;
};

export default function ListingDetailPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { saleId } = useParams();
  const user = useSelector(selectUser);
  const [formData, setFormData] = useState({
    title: '',
    platform: '',
    price: '',
    shippingCost: '',
    salesTax: '',
    transactionFee: '',
    packingCost: '',
    notes: '',
    saleDate: ''
  });
  const [inventoryItems, setInventoryItems] = useState([]);
  const [isInventoryLoading, setIsInventoryLoading] = useState(true);
  const [selectedInventoryId, setSelectedInventoryId] = useState('');
  const [selectedInventoryQty, setSelectedInventoryQty] = useState(1);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // Status changes move cards in and out of the collection, so the dropdown
  // only stages the change and the confirm bar commits it.
  const [pendingStatus, setPendingStatus] = useState(null);
  const [copiedSku, setCopiedSku] = useState(false);
  const hydratedSaleIdRef = useRef(null);
  const checklistSportDefault = useSelector(selectChecklistSportDefault);
  const checklistYearDefault = useSelector(selectChecklistYearDefault);
  const checklistManufacturerDefault = useSelector(selectChecklistManufacturerDefault);

  const { data: listing, isLoading: listingLoading, error: listingError } = useGetSaleQuery(
    { uid: user?.uid, saleId },
    { skip: !user?.uid || !saleId }
  );

  const [removeCardFromSale, { isLoading: isRemoving }] = useRemoveCardFromSaleMutation();
  const [updateSale, { isLoading: isUpdating }] = useUpdateSaleMutation();
  const [addSupplyToSale, { isLoading: isAddingSupply }] = useAddSupplyToSaleMutation();
  const [deleteSale, { isLoading: isDeleting }] = useDeleteSaleMutation();
  const [markSaleAsSold, { isLoading: isMarkingSold }] = useMarkSaleAsSoldMutation();
  const [revertSaleToListed, { isLoading: isReverting }] = useRevertSaleToListedMutation();

  const handleInventorySnapshot = useCallback((snapshot) => {
    const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setInventoryItems(items);
    setIsInventoryLoading(false);
  }, []);

  useEffect(() => {
    if (!user?.uid) {
      setInventoryItems([]);
      setIsInventoryLoading(false);
      return;
    }

    setIsInventoryLoading(true);
    const inventoryRef = collection(db, `users/${user.uid}/inventory`);
    const inventoryQuery = query(inventoryRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(
      inventoryQuery,
      handleInventorySnapshot,
      () => {
        dispatch(setSystemNotification('Error loading inventory'));
        setIsInventoryLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user?.uid, dispatch, handleInventorySnapshot]);

  const selectedInventoryItem = useMemo(() => {
    return inventoryItems.find(item => item.id === selectedInventoryId) || null;
  }, [inventoryItems, selectedInventoryId]);

  const selectedSupplyQty = useMemo(() => {
    if (!listing?.supplies || !selectedInventoryId) return 0;
    return listing.supplies
      .filter(item => item.inventoryItemId === selectedInventoryId)
      .reduce((sum, item) => sum + (parseInt(item.qtyUsed, 10) || 0), 0);
  }, [listing?.supplies, selectedInventoryId]);

  useEffect(() => {
    if (selectedInventoryId) {
      setSelectedInventoryQty(1);
    }
  }, [selectedInventoryId]);

  // Hydrate the form once per sale. Supply and card mutations refetch the sale,
  // so re-running this on every `listing` change would discard unsaved edits.
  useEffect(() => {
    if (!listing || hydratedSaleIdRef.current === saleId) return;
    hydratedSaleIdRef.current = saleId;

    const price = listing.price || listing.salePrice || listing.listingPrice || 0;
    setFormData({
      title: listing.title || '',
      platform: listing.platform || '',
      price: price.toString(),
      shippingCost: (listing.shippingCost || 0).toString(),
      salesTax: (listing.salesTax || 0).toString(),
      transactionFee: (
        (parseFloat(listing.transactionFee) || 0) + (parseFloat(listing.platformFee) || 0)
      ).toString(),
      packingCost: (listing.packingCost || 0).toString(),
      notes: listing.notes || '',
      saleDate: toDateInputValue(listing.saleDate)
    });
  }, [listing, saleId]);

  // A sale can hold a platform this page does not offer, and a native select
  // renders blank when its value has no matching option.
  const platformOptions = useMemo(() => {
    const current = formData.platform?.trim();
    if (!current || PLATFORM_OPTIONS.includes(current)) return PLATFORM_OPTIONS;
    return [...PLATFORM_OPTIONS, current];
  }, [formData.platform]);

  // packingCost is owned by the supplies transaction, not the form, so it always
  // tracks the server value rather than being hydrated once.
  const listingPackingCost = listing?.packingCost;
  useEffect(() => {
    if (listingPackingCost === undefined) return;
    setFormData(prev => ({
      ...prev,
      packingCost: (listingPackingCost || 0).toString()
    }));
  }, [listingPackingCost]);

  const handleBackClick = () => {
    navigate('/transactions/tracker');
  };

  const handleChecklistsClick = () => {
    navigate(`/checklists/${checklistSportDefault}/${checklistYearDefault}/${checklistManufacturerDefault}`);
  };

  const handleCardClick = (card, path) => {
    // Navigate to the specific card in the checklist
    if (card.checklistPath) {
      navigate(card.checklistPath);
    }
  };

  const handleRemoveFromListing = async (cardId) => {
    if (!user?.uid || !saleId) return;

    try {
      await removeCardFromSale({
        uid: user.uid,
        saleId,
        cardId
      }).unwrap();
      dispatch(setSystemNotification('Card removed from sale'));
    } catch (error) {
      dispatch(setSystemNotification(`Error removing card: ${error.message || 'Unknown error'}`));
    }
  };

  const sanitizeNumericInput = (value) => {
    // Remove all characters except digits and decimal point (including $)
    // Allow only one decimal point
    const cleaned = value.replace(/[^\d.]/g, '');
    const parts = cleaned.split('.');
    if (parts.length > 2) {
      // If more than one decimal point, keep only the first one
      return parts[0] + '.' + parts.slice(1).join('');
    }
    return cleaned;
  };

  const roundToNearestPenny = (value) => {
    if (!value || value === '') return '';
    const num = parseFloat(value);
    if (isNaN(num)) return '';
    // Round to 2 decimal places (nearest penny)
    return (Math.round(num * 100) / 100).toFixed(2);
  };

  const formatCurrencyDisplay = (value) => {
    if (!value || value === '') return '';
    return `$${value}`;
  };

  const formatDisplayDate = (value) => {
    const date = typeof value?.toDate === 'function' ? value.toDate() : new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const getNumericDisplayValue = (field) => {
    const value = formData[field];
    if (!value || value === '') return '';
    return formatCurrencyDisplay(value);
  };

  const handleNumericBlur = (field, value) => {
    const rounded = roundToNearestPenny(value);
    if (rounded !== value) {
      handleFieldChange(field, rounded);
    }
  };

  const handleFieldChange = (field, value) => {
    // For numeric fields, sanitize the input (remove $ and other non-numeric chars)
    const numericFields = ['price', 'shippingCost', 'salesTax', 'transactionFee', 'packingCost'];
    let sanitizedValue = value;

    if (numericFields.includes(field)) {
      // Remove $ prefix if user typed it
      sanitizedValue = sanitizeNumericInput(value);
    }

    setFormData(prev => ({
      ...prev,
      [field]: sanitizedValue
    }));
  };

  const handleAddInventoryItemToListing = async () => {
    if (!user?.uid || !saleId || !selectedInventoryItem) return;

    const maxRemovable = Math.max(0, selectedSupplyQty);
    if (selectedInventoryQty < 0 && maxRemovable <= 0) {
      dispatch(setSystemNotification('No supplies available to remove.'));
      return;
    }
    if (selectedInventoryQty < 0 && Math.abs(selectedInventoryQty) > maxRemovable) {
      dispatch(setSystemNotification(`You can only remove up to ${maxRemovable}.`));
      return;
    }

    try {
      await addSupplyToSale({
        uid: user.uid,
        saleId,
        supplyData: {
          inventoryItemId: selectedInventoryItem.id,
          qtyUsed: selectedInventoryQty
        }
      }).unwrap();
      dispatch(setSystemNotification(
        selectedInventoryQty < 0 ? 'Inventory item removed from sale' : 'Inventory item added to sale'
      ));
    } catch (error) {
      dispatch(setSystemNotification(error.message || 'Error adding inventory item to sale'));
    }
  };

  const handleInventoryQtyChange = (delta) => {
    if (!selectedInventoryItem) return;
    const maxQty = Math.max(1, parseInt(selectedInventoryItem.quantity, 10) || 1);
    const maxRemovable = Math.max(0, selectedSupplyQty);
    setSelectedInventoryQty(prev => {
      let next = prev + delta;
      if (next === 0) {
        next = delta < 0 ? -1 : 1;
      }
      if (next > maxQty) {
        next = maxQty;
      }
      if (next < 0) {
        if (maxRemovable === 0) {
          return prev;
        }
        const minQty = -maxRemovable;
        next = Math.max(next, minQty);
      }
      return next;
    });
  };

  // packingCost is deliberately excluded: it is owned by the supplies transaction.
  const buildSaleFields = () => ({
    title: formData.title.trim() || '',
    platform: formData.platform.trim(),
    price: parseFloat(formData.price) || 0,
    shippingCost: parseFloat(formData.shippingCost) || 0,
    salesTax: parseFloat(formData.salesTax) || 0,
    transactionFee: parseFloat(formData.transactionFee) || 0,
    platformFee: 0,
    notes: formData.notes.trim() || '',
    saleDate: fromDateInputValue(formData.saleDate)
  });

  const handleSave = async () => {
    if (!user?.uid || !saleId) return;

    try {
      await updateSale({
        uid: user.uid,
        saleId,
        updateData: buildSaleFields()
      }).unwrap();

      dispatch(setSystemNotification('Sale updated successfully!'));
    } catch (error) {
      dispatch(setSystemNotification(`Error updating sale: ${error.message || 'Unknown error'}`));
    }
  };

  const handleMarkAsSold = async () => {
    // Saves the current form values in the same action, so the numbers on
    // screen are the ones locked in with the sale.
    const saleFields = buildSaleFields();
    const { release } = await markSaleAsSold({
      uid: user.uid,
      saleId,
      saleFields: { ...saleFields, saleDate: saleFields.saleDate || new Date() }
    }).unwrap();

    const unreleased = (release?.notInCollection || 0) + (release?.skipped || 0);
    return unreleased > 0
      ? `Marked as sold. ${release.removed} card(s) removed from your collection, ${unreleased} could not be matched.`
      : `Marked as sold. ${release?.removed || 0} card(s) removed from your collection.`;
  };

  const handleRevertToListed = async () => {
    const { restore } = await revertSaleToListed({ uid: user.uid, saleId }).unwrap();

    return restore?.restored
      ? `Reopened as listed. ${restore.restored} card(s) returned to your collection.`
      : 'Reopened as listed. No cards needed to be returned to your collection.';
  };

  const handleConfirmStatusChange = async () => {
    if (!user?.uid || !saleId || !pendingStatus) return;

    try {
      const message = isSold(pendingStatus)
        ? await handleMarkAsSold()
        : await handleRevertToListed();

      // Either direction moves cards, so cached collection views are stale.
      dispatch(invalidateCollection());
      dispatch(setSystemNotification(message));
    } catch (error) {
      dispatch(setSystemNotification(`Error updating status: ${error.message || 'Unknown error'}`));
    } finally {
      setPendingStatus(null);
    }
  };

  const handleCopySku = async (sku) => {
    if (user?.uid && saleId) {
      try {
        await syncGroupCodePreviewForSale(user.uid, saleId);
      } catch {
        // Preview backfill is best-effort; the SKU should still copy.
      }
    }
    try {
      await navigator.clipboard.writeText(sku);
      setCopiedSku(true);
      setTimeout(() => setCopiedSku(false), 2000);
    } catch (error) {
      dispatch(setSystemNotification('Could not copy SKU'));
    }
  };

  const handleDeleteListing = async () => {
    if (!user?.uid || !saleId) return;

    try {
      await deleteSale({
        uid: user.uid,
        saleId
      }).unwrap();
      dispatch(setSystemNotification('Sale deleted'));
      navigate('/transactions/tracker');
    } catch (error) {
      dispatch(setSystemNotification(`Error deleting sale: ${error.message || 'Unknown error'}`));
    } finally {
      setShowDeleteConfirm(false);
    }
  };

  const overviewPrice = parseFloat(formData.price) || 0;
  const overviewShipping = parseFloat(formData.shippingCost) || 0;
  const overviewSalesTax = parseFloat(formData.salesTax) || 0;
  const overviewTransaction = parseFloat(formData.transactionFee) || 0;
  const overviewSupplies = parseFloat(formData.packingCost) || 0;
  const supplyBreakdown = listing?.supplies || [];
  const supplyBreakdownTotal = supplyBreakdown.reduce((sum, item) => {
    return sum + (parseFloat(item.totalCost) || 0);
  }, 0);
  const suppliesTotalToUse = supplyBreakdown.length > 0 ? supplyBreakdownTotal : overviewSupplies;
  const overviewSaleFigures = {
    price: overviewPrice,
    salesTax: overviewSalesTax,
    shippingCost: overviewShipping,
    transactionFee: overviewTransaction,
    packingCost: suppliesTotalToUse
  };
  const overviewNetProfit = calculateNetProfit(overviewSaleFigures);

  if (listingLoading) {
    return (
      <ListingDetailContainer>
        <LoadingSpinner>Loading sale...</LoadingSpinner>
      </ListingDetailContainer>
    );
  }

  if (listingError || !listing) {
    return (
      <ListingDetailContainer>
        <ErrorMessage>Error loading sale or sale not found</ErrorMessage>
      </ListingDetailContainer>
    );
  }

  const listingCards = listing.cards || [];
  const saleStatus = normalizeSaleStatus(listing.status);
  const soldAlready = isSold(listing.status);
  const cardHierarchy = transformListingCardsToHierarchy(listingCards, soldAlready);
  const isChangingStatus = isMarkingSold || isReverting;
  const busy = isChangingStatus || isDeleting;
  const confirmingStatus = !!pendingStatus;
  const daysListed = listing.listingDate
    ? Math.max(0, Math.floor((Date.now() - new Date(listing.listingDate).getTime()) / 86400000))
    : null;

  const statusConfirmMessage = isSold(pendingStatus)
    ? `Marking sold saves your current values and removes ${listingCards.length} card(s) from your collection.`
    : 'Reopening returns the cards this sale removed to your collection. Your figures are kept.';

  return (
    <ListingDetailContainer>
      <Header>
        <HeaderLeftContent>
          <SecondaryBtn title="BACK" onClick={handleBackClick} />
          <ListingTitle>{listing.title}</ListingTitle>
        </HeaderLeftContent>
        <HeaderRightContent>
          <ListingStats>
            <StatusControl $sold={isSold(pendingStatus ?? listing.status)}>
              <InputDropdown
                id="status"
                size="compact"
                value={pendingStatus ?? saleStatus}
                onChange={(value) => setPendingStatus(value === saleStatus ? null : value)}
                options={[SALE_STATUSES.LISTED, SALE_STATUSES.SOLD]}
              />
            </StatusControl>
            <CardCountPill value={listingCards.length || 0} />
          </ListingStats>
          {!showDeleteConfirm && !confirmingStatus && (
            <>
              <SecondaryBtn
                type="critical"
                title="DELETE SALE"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={busy}
              />
              <PrimaryBtn
                title={isUpdating ? "SAVING..." : "SAVE"}
                onClick={handleSave}
                disabled={isUpdating || busy}
              />
            </>
          )}
          {confirmingStatus && (
            <>
              <ConfirmMessage>{statusConfirmMessage}</ConfirmMessage>
              <PrimaryBtn
                title={isChangingStatus
                  ? 'WORKING...'
                  : (isSold(pendingStatus) ? 'CONFIRM SOLD' : 'CONFIRM REOPEN')}
                onClick={handleConfirmStatusChange}
                disabled={isChangingStatus}
              />
              <SecondaryBtn
                title="CANCEL"
                onClick={() => setPendingStatus(null)}
                disabled={isChangingStatus}
              />
            </>
          )}
          {showDeleteConfirm && (
            <>
              <ConfirmMessage>
                {soldAlready
                  ? 'Deleting will not return these cards to your collection.'
                  : 'This permanently deletes the sale.'}
              </ConfirmMessage>
              <PrimaryBtn
                type="critical"
                title="CONFIRM DELETE"
                onClick={handleDeleteListing}
                disabled={isDeleting}
              />
              <SecondaryBtn
                title="CANCEL"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
              />
            </>
          )}
        </HeaderRightContent>
      </Header>

      <MetaStrip>
        <MetaItem>
          <MetaLabel>Listed</MetaLabel>
          <MetaValue>{listing.listingDate ? formatDisplayDate(listing.listingDate) : '—'}</MetaValue>
        </MetaItem>
        {!soldAlready && daysListed !== null && (
          <MetaItem>
            <MetaLabel>Days Listed</MetaLabel>
            <MetaValue>{daysListed}</MetaValue>
          </MetaItem>
        )}
        {soldAlready && (
          <MetaItem>
            <MetaLabel>Sold</MetaLabel>
            <MetaValue>{listing.saleDate ? formatDisplayDate(listing.saleDate) : '—'}</MetaValue>
          </MetaItem>
        )}
        {listing.listingFormat && (
          <MetaItem>
            <MetaLabel>Format</MetaLabel>
            <MetaValue>{listing.listingFormat}</MetaValue>
          </MetaItem>
        )}
        {listing.ebaySku && (
          <MetaItem>
            <MetaLabel>eBay SKU</MetaLabel>
            <CopyableMetaValue
              as="button"
              type="button"
              onClick={() => handleCopySku(listing.ebaySku)}
              title="Copy SKU"
            >
              {listing.ebaySku}
              <span>{copiedSku ? 'Copied' : 'Copy'}</span>
            </CopyableMetaValue>
          </MetaItem>
        )}
      </MetaStrip>

      <BodyContent>
        <BodyLeftContent>
          <InfoRow>
            <InputBar
              label="Sale Title"
              type="text"
              id="title"
              value={formData.title}
              updateValue={(value) => handleFieldChange('title', value)}
            />
          </InfoRow>
          <InfoRow>
            <InputBar
              label="Sold Date"
              type="date"
              id="saleDate"
              value={formData.saleDate}
              updateValue={(value) => handleFieldChange('saleDate', value)}
            />
            <InputBar
              label="Order Total"
              type="text"
              inputMode="decimal"
              id="price"
              value={getNumericDisplayValue('price')}
              updateValue={(value) => handleFieldChange('price', value)}
              onBlur={() => handleNumericBlur('price', formData.price)}
            />
          </InfoRow>
          <InfoRow>
            <InputBar
              label="Sales Tax"
              type="text"
              inputMode="decimal"
              id="salesTax"
              value={getNumericDisplayValue('salesTax')}
              updateValue={(value) => handleFieldChange('salesTax', value)}
              onBlur={() => handleNumericBlur('salesTax', formData.salesTax)}
            />
            <InputBar
              label="Transaction Fee"
              type="text"
              inputMode="decimal"
              id="transactionFee"
              value={getNumericDisplayValue('transactionFee')}
              updateValue={(value) => handleFieldChange('transactionFee', value)}
              onBlur={() => handleNumericBlur('transactionFee', formData.transactionFee)}
            />
          </InfoRow>
          <InfoRow>
            <InputBar
              label="Shipping Cost"
              type="text"
              inputMode="decimal"
              id="shippingCost"
              value={getNumericDisplayValue('shippingCost')}
              updateValue={(value) => handleFieldChange('shippingCost', value)}
              onBlur={() => handleNumericBlur('shippingCost', formData.shippingCost)}
            />
          </InfoRow>
          <InfoRow>
            <div style={{ width: '100%' }}>
              <InputDropdown
                id="platform"
                value={formData.platform}
                onChange={(value) => handleFieldChange('platform', value)}
                options={platformOptions}
                placeholder="Select platform"
              />
            </div>
          </InfoRow>
          <InfoRow>
            <InputTextfield
              label="Notes"
              id="notes"
              value={formData.notes}
              updateValue={(value) => handleFieldChange('notes', value)}
              rows={3}
              maxLength={500}
            />
          </InfoRow>
        </BodyLeftContent>
        <InventoryDetailContent>
          <InputDropdown
            id="inventory-item"
            label="Inventory Item"
            value={selectedInventoryId}
            onChange={(value) => setSelectedInventoryId(value)}
            placeholder={isInventoryLoading ? 'Loading inventory...' : 'Select inventory item'}
          >
            {inventoryItems.map(item => (
              <option key={item.id} value={item.id}>
                {item.itemName}
              </option>
            ))}
          </InputDropdown>

          {selectedInventoryItem && (
            <InventorySelectionDetails>
              <QtyTicker>
                <QtyButton
                  $disabled={selectedInventoryQty <= 1 && Math.max(0, selectedSupplyQty) === 0}
                  onClick={() => handleInventoryQtyChange(-1)}
                >
                  <img src={minusIcon} alt="decrease quantity" />
                </QtyButton>
                <QtyValue>{selectedInventoryQty}</QtyValue>
                <QtyButton
                  $disabled={selectedInventoryQty >= (parseInt(selectedInventoryItem.quantity, 10) || 1)}
                  onClick={() => handleInventoryQtyChange(1)}
                >
                  <img src={addIcon} alt="increase quantity" />
                </QtyButton>
              </QtyTicker>

              <CostRow>
                <CostLabel>Cost / Unit</CostLabel>
                <CostValue>
                  {formatCurrency(
                    (parseFloat(selectedInventoryItem.totalCost) || 0) /
                      Math.max(1, parseInt(selectedInventoryItem.quantity, 10) || 1)
                  )}
                </CostValue>
              </CostRow>
              <CostRow>
                <CostLabel>Total Cost</CostLabel>
                <CostValue>
                  {formatCurrency(
                    ((parseFloat(selectedInventoryItem.totalCost) || 0) /
                      Math.max(1, parseInt(selectedInventoryItem.quantity, 10) || 1)) * selectedInventoryQty
                  )}
                </CostValue>
              </CostRow>
              <PrimaryBtn
                title={isAddingSupply ? 'UPDATING...' : (selectedInventoryQty < 0 ? 'REMOVE ITEM(S)' : 'ADD ITEM(S)')}
                onClick={handleAddInventoryItemToListing}
                disabled={isAddingSupply}
              />
            </InventorySelectionDetails>
          )}
        </InventoryDetailContent>
        <OverviewDetailContent>
          <OverviewTitle>Overview</OverviewTitle>
          <CostRow>
            <CostLabel>Order Total</CostLabel>
            <CostValue>{formatCurrency(overviewPrice)}</CostValue>
          </CostRow>
          {/* A listed sale has no figures yet, so zero rows are noise until it sells */}
          {(soldAlready || overviewSalesTax > 0) && (
            <CostRow>
              <CostLabel>Sales Tax</CostLabel>
              <CostValue>- {formatCurrency(overviewSalesTax)}</CostValue>
            </CostRow>
          )}
          {(soldAlready || overviewTransaction > 0) && (
            <CostRow>
              <CostLabel>Transaction Fee</CostLabel>
              <CostValue>- {formatCurrency(overviewTransaction)}</CostValue>
            </CostRow>
          )}
          {(soldAlready || overviewShipping > 0) && (
            <CostRow>
              <CostLabel>Shipping Cost</CostLabel>
              <CostValue>- {formatCurrency(overviewShipping)}</CostValue>
            </CostRow>
          )}
          <CostRow style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', paddingTop: '16px' }}>
          <CostLabel><b><u>Supplies Cost:</u></b></CostLabel>
            <CostValue>- {formatCurrency(suppliesTotalToUse)}</CostValue>
          </CostRow>
          {supplyBreakdown.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '-4px', paddingLeft: '16px' }}>
              {supplyBreakdown.map((item) => (
                <CostRow key={item.id}>
                  <CostLabel>
                    {item.itemName || 'Supply'} × {item.qtyUsed}
                  </CostLabel>
                  <CostValue>- {formatCurrency(item.totalCost)}</CostValue>
                </CostRow>
              ))}
            </div>
          )}
          <CostRow>
            <CostLabel>{soldAlready ? 'Net Profit' : 'Projected Profit'}</CostLabel>
            <CostValue>{formatCurrency(overviewNetProfit)}</CostValue>
          </CostRow>
        </OverviewDetailContent>
      </BodyContent>

      {listingCards.length > 0 ? (
        <CollapsibleChecklist
          data={cardHierarchy}
          onCardClick={handleCardClick}
          onRemoveCard={handleRemoveFromListing}
          isRemoving={isRemoving}
          // A sold sale's cards have been released from the collection, and that
          // record lives on the card. Reopen the sale to change what is in it.
          canRemove={!soldAlready}
          removeLabel="REMOVE FROM SALE"
        />
      ) : (
        <EmptyState>
          <h2>No cards in this sale</h2>
          <p>Open a card in its checklist and choose Add to Sales to add it here.</p>
          <PrimaryBtn
            title="BROWSE CHECKLISTS"
            onClick={handleChecklistsClick}
          />
        </EmptyState>
      )}
    </ListingDetailContainer>
  );
}

